import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { FormikErrors } from "formik";

import FormField from "../../../components/form-field/FormField";
import { translationService } from "../../../services/translation/TranslationService";
import { RequiredValidatorModel } from "../../../services/form/validators/required/RequiredValidator";
import { Dropdown, DropdownOption } from "../../../components/dropdown/Dropdown";
import { SupportTag } from "../../../components/grid-tag/Tags";
import { EmploymentStatus } from "../../../services/user/EmploymentStatus";
import { Role } from "../../../services/user/Role";

interface EditUserRoleProps {
  role: number;
  roleOptions: Array<Role>;
  setFieldValue: (field: string, value: any) => Promise<void | FormikErrors<any>>;
}

export const EditUserRole = observer((props: EditUserRoleProps) => {
  const { role, roleOptions, setFieldValue } = props;

  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));

  const optionsList: DropdownOption[] = roleOptions.map((option) => {
    return {
      id: option.id,
      name: translationService.translate(option.translationKey),
      isDefault: option.default,
    };
  });

  return (
    <FormField
      id="user-role"
      labelKey="users.EditUserRole.role"
      required
      className="select"
      fieldName="role"
      fieldValue={role}
      validator={requiredValidator}
      errorName="role"
      children={() => (
        <Dropdown
          selectedOption={optionsList.find((o) => o.id === role) ?? { id: "", name: "" }}
          options={optionsList}
          fieldName="role"
          setFieldValue={(field: string, value: any) => {
            setFieldValue(field, value.id);
          }}
        />
      )}
    />
  );
});

interface EditEmploymentStatusProps {
  employmentStatus: EmploymentStatus;
  options: Array<EmploymentStatus>;
  onChange: (employeeType: EmploymentStatus) => void;
  setFieldValue: Function;
  organizationName: string;
}

export const EditEmploymentStatus = observer((props: EditEmploymentStatusProps) => {
  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));
  const { employmentStatus, options, onChange, setFieldValue } = props;

  const mappedStatuses = options.map((option) => ({
    ...option,
    name: option.displayName,
    isDefault: option.isDefault,
  }));

  const mappedStatus = mappedStatuses.find((status) => status.id === employmentStatus.id) ?? null;

  return (
    <FormField
      id="employmentStatus"
      labelKey="general.user.employment-status"
      required
      className="select"
      fieldName="employmentStatus"
      fieldValue={employmentStatus}
      validator={requiredValidator}
      children={() => (
        <div className="form-element-container">
          <Dropdown
            selectedOption={mappedStatus}
            options={mappedStatuses}
            onChange={onChange}
            fieldName="employmentStatus"
            setFieldValue={setFieldValue}
          />
        </div>
      )}
      errorName="employmentStatus"
    />
  );
});

interface EditFirstNameProps {
  firstName: string;
  readonly?: boolean;
}

export function EditFirstName(props: EditFirstNameProps) {
  return (
    <FormField
      id="first-name"
      labelKey="general.first-name"
      component="input"
      className="input"
      fieldName="firstName"
      fieldValue={props.firstName}
      placeholderKey="general.first-name"
      errorName="firstName"
      readOnly={props.readonly}
    />
  );
}

interface EditLastNameProps {
  lastName: string;
  readonly?: boolean;
}

export function EditLastName(props: EditLastNameProps) {
  return (
    <FormField
      id="last-name"
      labelKey="general.last-name"
      component="input"
      className="input"
      fieldName="lastName"
      fieldValue={props.lastName}
      placeholderKey="general.last-name"
      errorName="lastName"
      readOnly={props.readonly}
    />
  );
}

interface UserNameProps {
  userName: string;
  readonly?: boolean;
  supportTag?: boolean;
}

export function EditUserName(props: UserNameProps) {
  return (
    <FormField
      id="user-name"
      labelKey="general.user-name"
      component="input"
      className="input"
      fieldName="userName"
      fieldValue={props.userName}
      placeholderKey="general.user-name"
      errorName="userName"
      readOnly={props.readonly}
    />
  );
}

interface StatusInCompanyProps {
  status: string;
  readonly?: boolean;
}

export function EditStatusInCompany(props: StatusInCompanyProps) {
  return (
    <FormField
      id="status-in-organization"
      labelKey="general.users.status-in-organization"
      component="input"
      className="input"
      fieldName="statusInCompany"
      fieldValue={props.status}
      placeholderKey="general.users.status-in-organization"
      errorName="statusInCompany"
      readOnly={props.readonly}
    />
  );
}

export function DisplayUserName(props: UserNameProps) {
  return (
    <div className="display-username-field">
      <FormField
        id="user-name"
        labelKey="general.user-name"
        readOnly
        fieldValue={props.userName || "-"}
        accessoryElement={props.supportTag ? <SupportTag /> : undefined}
      />
    </div>
  );
}

interface NameProps {
  name: string;
}

export function DisplayFirstName(props: NameProps) {
  return (
    <FormField
      id="first-name"
      labelKey="general.first-name"
      readOnly
      fieldValue={props.name || "-"}
    />
  );
}

export function DisplayLastName(props: NameProps) {
  return (
    <FormField
      id="last-name"
      labelKey="general.last-name"
      readOnly
      fieldValue={props.name || "-"}
    />
  );
}

interface PhoneProps {
  phone: string;
  inOrganization?: boolean;
}

export function DisplayPhone(props: PhoneProps) {
  return (
    <FormField
      id="phone"
      labelKey={props.inOrganization ? "organization.organization-phone-number" : "general.phone"}
      readOnly
      fieldValue={props.phone || "-"}
    />
  );
}

interface EmailProps {
  email: string;
}

export function DisplayEmail(props: EmailProps) {
  return <FormField id="email" labelKey="general.email" readOnly fieldValue={props.email || "-"} />;
}

interface RoleProps {
  role: Role;
}

export const DisplayUserRole = observer((props: RoleProps) => {
  return (
    <FormField
      id="role"
      labelKey="users.EditUserRole.role"
      readOnly
      fieldValue={translationService.translate(props.role.translationKey)}
    />
  );
});

export function DisplayPassword() {
  return (
    <FormField
      id="display-password"
      labelKey="general.password"
      readOnly
      fieldValue="•••••••••••••"
    />
  );
}

interface EmployeeTypeProps {
  employmentStatus: EmploymentStatus;
  organizationName: string;
}

export const DisplayEmploymentStatus = observer((props: EmployeeTypeProps) => {
  return (
    <FormField
      id="employment-status"
      labelKey="general.user.employment-status"
      readOnly
      fieldValue={props.employmentStatus.displayName}
    />
  );
});

interface GeneralManagerNameProps {
  name: string;
}

export function DisplayGeneralManagerFirstName(props: GeneralManagerNameProps) {
  return (
    <FormField
      id="gm-first-name"
      labelKey="general.general-manager-first-name"
      readOnly
      fieldValue={props.name || "-"}
    />
  );
}

export function DisplayGeneralManagerLastName(props: GeneralManagerNameProps) {
  return (
    <FormField
      id="gm-last-name"
      labelKey="general.general-manager-last-name"
      readOnly
      fieldValue={props.name || "-"}
    />
  );
}

interface WebsiteURLProps {
  websiteURL?: string;
}

export function DisplayWebsiteURL(props: WebsiteURLProps) {
  return (
    <FormField
      id="website-url"
      labelKey="general.website-url"
      readOnly
      fieldValue={props.websiteURL || "-"}
    />
  );
}
